// License (/license) — view the key's tier, entitlements and machine binding;
// activate/copy/download the Ed25519 key; transfer (re-issue) with cooldown handling.
// One-time model: NO expiry, NO renewal. An account without a key gets the keyless export
// limits stated as fact plus a link to /buy — shown ONLY where this API actually sells
// (GET /pricing listing a paid tier, the same gate /buy uses), so the link can never point
// at a purchase that would answer 410. Every key, new or old, keeps the behaviour below.
// Card anatomy: shadcn-ui/ui apps/v4/registry/new-york-v4/ui/card.tsx — MIT.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Glyph } from "@/ui";
import { dataClass } from "@ahcut/ui/recipes";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  CardTitle,
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
  Field,
  LicenseStatus,
} from "@/ui";
import { PageHeader } from "@/components/PageHeader";
import { CopyButton } from "@/components/CopyButton";
import { Loading, ErrorState } from "@/components/States";
import { cn } from "@/lib/cn";
import { useAsync } from "@/lib/useAsync";
import { listLicenses, activateLicense, reissueLicense } from "@/lib/license";
import { getPricing, hasPaidTier } from "@/lib/pricing";
import { humanizeFeatures, tierLabel, formatDate, formatDateTime } from "@/lib/format";
import { ApiError } from "@/lib/api";
import { toError } from "@/lib/errors";
import { MARKETING_BASE } from "@/lib/config";
import { supportMailto } from "@/lib/support";
import type { License, LicenseKeyResponse, Pricing } from "@/lib/types";
import { KeylessExportLimits } from "@/components/KeylessExportLimits";

const PAGE_TITLE = "Lisensi";
const DOWNLOAD_URL = `${MARKETING_BASE}/unduh`;

// Error/note ids the Machine ID inputs reference through aria-describedby.
const ACTIVATE_ERROR_ID = "license-activate-error";
const MISMATCH_NOTE_ID = "license-machine-mismatch";
const TRANSFER_ERROR_ID = "license-transfer-error";

/** The typed Machine ID itself was refused (aria-invalid). A rate limit or a server
 *  failure is described on the input but does not mark it invalid. */
function isMachineIdError(err: Error | null): boolean {
  return err instanceof ApiError && (err.code === "validation" || err.code === "machine_mismatch");
}

/** The code as a plain .txt: Notepad and TextEdit open it alike, whereas a Mac
 *  hands a ".key" file to Keynote. The app takes the code by paste only. */
function downloadKey(code: string, tier: string) {
  const blob = new Blob([code], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ahcut-license-${tier}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function LicensePage() {
  const { data: licenses, loading, error, reload } = useAsync<License[]>(
    (signal) => listLicenses(signal),
    [],
  );

  const paid = useMemo(
    () => (licenses ?? []).find((l) => l.tier !== "free" && !l.revoked_at) ?? null,
    [licenses],
  );
  const revoked = useMemo(
    () => (licenses ?? []).find((l) => l.tier !== "free" && l.revoked_at) ?? null,
    [licenses],
  );

  // Full-page states only while there is nothing to show yet: a RELOAD (e.g.
  // handleResult → onChanged after activate/reissue) keeps the previous data
  // rendered — an early return here would unmount LicenseManager and destroy
  // the just-minted activation code before the user can copy it. The h1 renders
  // in every state (ui/DESIGN.md §10).
  if (loading && !licenses) {
    return (
      <>
        <PageHeader title={PAGE_TITLE} />
        <Loading label="Memuat lisensi…" />
      </>
    );
  }
  if (error) {
    return (
      <>
        <PageHeader title={PAGE_TITLE} />
        <ErrorState error={error} onRetry={reload} />
      </>
    );
  }

  // Fragment: the shell's content column (Layout app-main-inner) sets the rhythm.
  return (
    <>
      <PageHeader
        title={PAGE_TITLE}
        lede={
          paid
            ? "Kunci lisensimu berlaku untuk semua pembaruan ahcut. Aktivasi ke perangkat, tampilkan kodenya, atau pindahkan ke perangkat lain."
            : "ahcut gratis untuk semua pengguna. Halaman ini untuk kunci lisensi Pro: aktivasi ke perangkat, kode kunci, dan pindah perangkat."
        }
      />

      {paid ? (
        <LicenseManager license={paid} onChanged={reload} />
      ) : (
        <FreeState revoked={revoked} />
      )}
    </>
  );
}

function FreeState({ revoked }: { revoked: License | null }) {
  // The purchase link is gated exactly like /buy gates itself: a paid tier in the live
  // GET /pricing response (lib/pricing.ts). A dashboard pointed at an API with sales off
  // — and a read that failed or has not landed yet — simply shows no Beli button, never
  // a dead one.
  const { data: pricing } = useAsync<Pricing>((s) => getPricing(s), []);
  const canBuy = hasPaidTier(pricing);

  return (
    <>
      {revoked && (
        <Alert tone="danger" role="alert" title="Lisensi dicabut">
          Lisensi {tierLabel(revoked.tier)}-mu dicabut, biasanya karena pembayarannya dibatalkan.{" "}
          <a href={supportMailto(`Lisensi dicabut — ${revoked.id}`)}>Hubungi dukungan</a> jika ini
          keliru.
        </Alert>
      )}
      <Card>
        <LicenseStatus tier="Tanpa kunci lisensi" />
        <p className="text-fg-1">
          Tanpa kunci, video yang kamu ekspor saat ini mengikuti batas berikut.
        </p>
        <KeylessExportLimits />
        <div className="flex flex-col gap-3 border-t border-line pt-4">
          <p className="text-sm text-fg-1">
            Kunci yang sudah dibeli tetap berlaku. Kunci milik akun ini tampil di halaman ini,
            lengkap dengan aktivasi ke perangkat. Kalau kuncimu dibeli dengan akun Google lain,
            keluar lalu masuk dengan akun itu.
          </p>
          <div className="flex flex-wrap gap-3">
            {canBuy && (
              <Button asChild variant="primary">
                <Link to="/buy?tier=pro">
                  <Glyph name="cart" />
                  Beli Pro
                </Link>
              </Button>
            )}
            <Button asChild variant="quiet">
              <a href={DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">
                <Glyph name="download" />
                Unduh ahcut
              </a>
            </Button>
          </div>
        </div>
      </Card>
    </>
  );
}

type BusyAction = "activate" | "reissue" | "fetch" | null;

function LicenseManager({ license, onChanged }: { license: License; onChanged: () => void }) {
  const bound = license.machine_id != null && license.machine_id !== "";
  const features = humanizeFeatures(license.features);

  const [minted, setMinted] = useState<LicenseKeyResponse | null>(null);
  // Separate inputs: Activate and Transfer both render for an unbound license —
  // one shared value once let a paste meant for Activate submit a Transfer,
  // silently burning the once-per-30-days reissue quota.
  const [activateInput, setActivateInput] = useState("");
  const [transferInput, setTransferInput] = useState("");
  const [busy, setBusy] = useState<BusyAction>(null);
  const [activateError, setActivateError] = useState<Error | null>(null);
  const [transferError, setTransferError] = useState<Error | null>(null);
  // Local 429 bridge until reload; the server's proactive value is authoritative.
  const [transferBlocked429, setTransferBlocked429] = useState<string | null>(null);

  // Server state is authoritative: a reload without the field means the cooldown
  // ended — drop the local 429 bridge so Transfer re-enables without a page reload.
  useEffect(() => {
    if (!license.reissue_next_allowed_at) setTransferBlocked429(null);
  }, [license.reissue_next_allowed_at]);

  const reissueBlockedUntil = license.reissue_next_allowed_at ?? transferBlocked429;
  const machineMismatch =
    activateError instanceof ApiError && activateError.code === "machine_mismatch";

  function handleResult(res: LicenseKeyResponse) {
    setMinted(res);
    setActivateError(null);
    setTransferError(null);
    onChanged();
  }

  // What the user pastes into the desktop app. With `license_id` it is the
  // activation BLOB (same JSON shape the desktop stores), which keeps the
  // online revocation check working for manual activations; a bare key
  // (older API without license_id) still activates but stays offline-only.
  const activationCode = minted
    ? minted.license_id
      ? JSON.stringify({ key: minted.key, license_id: minted.license_id })
      : minted.key
    : null;

  async function doActivate(machineId: string) {
    setBusy("activate");
    setActivateError(null);
    try {
      handleResult(await activateLicense(machineId));
      setActivateInput("");
    } catch (err) {
      setActivateError(toError(err));
    } finally {
      setBusy(null);
    }
  }

  async function fetchExistingKey() {
    if (!license.machine_id) return;
    setBusy("fetch");
    setActivateError(null);
    try {
      handleResult(await activateLicense(license.machine_id));
    } catch (err) {
      setActivateError(toError(err));
    } finally {
      setBusy(null);
    }
  }

  async function doReissue(machineId: string) {
    setBusy("reissue");
    setTransferError(null);
    try {
      handleResult(await reissueLicense(machineId));
      setTransferInput("");
      setTransferBlocked429(null);
    } catch (err) {
      const e = toError(err);
      setTransferError(e);
      // Only a TRANSFER 429 sets the transfer cooldown — an activate 429
      // (10/minute) must never disable or relabel the transfer button.
      if (e instanceof ApiError && e.code === "rate_limited" && e.nextAllowedAt) {
        setTransferBlocked429(e.nextAllowedAt);
      }
    } finally {
      setBusy(null);
    }
  }

  const activateTrimmed = activateInput.trim();
  const transferTrimmed = transferInput.trim();
  const activateDescribedBy =
    [activateError && ACTIVATE_ERROR_ID, machineMismatch && MISMATCH_NOTE_ID]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <>
      <Card>
        <LicenseStatus
          tier={tierLabel(license.tier)}
          active
          features={features}
          machineId={license.machine_id}
        />
        {/* major_version is provenance only — never a coverage limit (LICENSING.md R7). */}
        <DescriptionList className="border-t border-line pt-4">
          <DescriptionTerm>Diterbitkan untuk versi</DescriptionTerm>
          <DescriptionDetails className={dataClass}>v{license.major_version}</DescriptionDetails>
          <DescriptionTerm>Diterbitkan</DescriptionTerm>
          <DescriptionDetails className={dataClass}>{formatDate(license.issued_at)}</DescriptionDetails>
        </DescriptionList>
        <p className="text-sm text-fg-2">Informasi saja — lisensimu berlaku untuk semua versi.</p>
      </Card>

      {!bound ? (
        <Card>
          <CardHeader>
            <CardTitle>Aktivasi ke perangkat</CardTitle>
          </CardHeader>
          <p className="text-fg-1">
            Cara tercepat: di aplikasi ahcut, pilih <strong>License…</strong> di menu logo, lalu tekan{" "}
            <strong>I have Pro · Sign in</strong>. Tidak perlu salin-tempel.
          </p>
          <div>
            <Button asChild variant="primary">
              <a href="ahcut://activate">Buka ahcut &amp; aktifkan</a>
            </Button>
          </div>
          <p className="text-sm text-fg-2">
            Belum pasang aplikasinya?{" "}
            <a href={DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">
              Unduh di ahcut.com/unduh
            </a>
            . Aplikasi tidak terbuka? Pastikan sudah terpasang, atau pakai cara manual di bawah.
          </p>

          <div className="flex flex-col gap-4 border-t border-line pt-5">
            <h3 className="font-semibold text-fg-0">
              Cara manual (offline / tanpa browser di perangkat itu)
            </h3>
            <p className="text-sm text-fg-1">
              Kunci lisensi terikat ke satu perangkat. Salin <strong>Machine ID</strong> dari
              aplikasi desktop ahcut (halaman Account di Settings, bagian <em>Have a license key?</em>), tempel di sini, lalu aktivasi untuk
              menerbitkan kunci.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <Field
                className="flex-1"
                inputClassName="font-mono"
                label="Machine ID (dari aplikasi desktop)"
                value={activateInput}
                onChange={(e) => setActivateInput(e.target.value)}
                placeholder="mis. a1b2c3…"
                spellCheck={false}
                aria-describedby={activateDescribedBy}
                aria-invalid={isMachineIdError(activateError) || undefined}
              />
              <Button
                variant="secondary"
                disabled={!activateTrimmed || busy !== null}
                onClick={() => doActivate(activateTrimmed)}
              >
                {busy === "activate" ? "Mengaktivasi…" : "Aktivasi ke perangkat ini"}
              </Button>
            </div>
            {activateError && (
              <ErrorState variant="inline" id={ACTIVATE_ERROR_ID} error={activateError} live />
            )}
            {machineMismatch && (
              <p id={MISMATCH_NOTE_ID} className="text-sm text-fg-1" role="status">
                Kunci ini terikat ke perangkat lain. Untuk memindahkannya, pakai bagian{" "}
                <strong>Pindahkan ke perangkat lain</strong> di bawah (sekali per 30 hari).
              </p>
            )}
          </div>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Kunci untuk perangkat terikat</CardTitle>
          </CardHeader>
          <p className="text-fg-1">
            Lisensi ini terikat ke perangkat <code>{license.machine_id}</code>. Kunci hanya lolos
            verifikasi di perangkat tersebut. Tampilkan kunci untuk menyalin atau mengunduhnya.
          </p>
          {!minted && (
            <div>
              <Button variant="primary" disabled={busy !== null} onClick={fetchExistingKey}>
                {busy === "fetch" ? "Mengambil…" : "Tampilkan kunci"}
              </Button>
            </div>
          )}
          {activateError && <ErrorState variant="inline" error={activateError} live />}
        </Card>
      )}

      {activationCode && (
        <Card>
          <CardHeader>
            <CardTitle>Kode aktivasi</CardTitle>
          </CardHeader>
          <div
            role="group"
            aria-label="Kode aktivasi lisensi"
            className={cn(
              dataClass,
              "rounded-md border border-line bg-surface-1 px-3 py-2.5 text-sm leading-6 break-all text-fg-0 select-all",
            )}
          >
            {activationCode}
          </div>
          <div className="flex flex-wrap gap-3">
            <CopyButton value={activationCode} />
            <Button variant="quiet" onClick={() => downloadKey(activationCode, license.tier)}>
              <Glyph name="download" />
              Unduh .txt
            </Button>
          </div>
          <p className="text-sm text-fg-2">
            {minted?.license_id ? (
              <>
                Tempel kode ini di aplikasi desktop (halaman Account di Settings: buka <em>Have a license key?</em> lalu tekan <em>Activate key</em>).
                Kode berisi kunci Ed25519 dan ID lisensi, sehingga aplikasi tetap bisa memeriksa
                status pencabutan saat online. Kunci aman disimpan di OS keyring oleh aplikasi.
              </>
            ) : (
              <>
                Tempel kunci ini di aplikasi desktop (halaman Account di Settings: buka <em>Have a license key?</em> lalu tekan <em>Activate key</em>).
                Server belum menyertakan ID lisensi, jadi aplikasi tidak dapat memeriksa status
                pencabutan secara online untuk kunci ini. Kunci aman disimpan di OS keyring oleh
                aplikasi.
              </>
            )}
          </p>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Pindahkan ke perangkat lain</CardTitle>
        </CardHeader>
        <p className="text-fg-1">
          Ganti perangkat? Terbitkan ulang kunci untuk <strong>Machine ID</strong> baru. Batas:
          sekali per 30 hari. Kunci lama berhenti berlaku di perangkat sebelumnya.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <Field
            className="flex-1"
            inputClassName="font-mono"
            label="Machine ID perangkat baru"
            value={transferInput}
            onChange={(e) => setTransferInput(e.target.value)}
            placeholder="mis. d4e5f6…"
            spellCheck={false}
            aria-describedby={transferError ? TRANSFER_ERROR_ID : undefined}
            aria-invalid={isMachineIdError(transferError) || undefined}
          />
          <Button
            variant="secondary"
            disabled={!transferTrimmed || busy !== null || reissueBlockedUntil !== null}
            onClick={() => doReissue(transferTrimmed)}
          >
            {busy === "reissue" ? "Menerbitkan ulang…" : "Terbitkan ulang & pindahkan"}
          </Button>
        </div>
        {reissueBlockedUntil && (
          <div className="flex flex-col items-start gap-2">
            <Badge tone="neutral" dot>
              Transfer tersedia lagi {formatDateTime(reissueBlockedUntil)}
            </Badge>
            <p className="text-sm text-fg-2">
              Perangkat lama hilang/rusak?{" "}
              <a href={supportMailto(`Pelepasan binding — ${license.id}`)}>Hubungi dukungan</a>{" "}
              untuk pelepasan binding.
            </p>
          </div>
        )}
        {transferError && (
          <ErrorState variant="inline" id={TRANSFER_ERROR_ID} error={transferError} live />
        )}
      </Card>
    </>
  );
}
